import React, { useContext, useEffect, useState } from 'react';
import { ReportContext } from '../../context/ReportContext';
import { HeaderField } from '../../models/reportTemplate';
import { toDateString } from '../../services/dateUtils';

interface ActionPanelProps {
  /** The template's generation-editable header fields (manual and derived) —
   *  one pre-filled input each (generalises the old fixed childName/reportDate
   *  inputs via ReportContext.fieldValues). */
  editableFields?: HeaderField[];
  /** Untouched-input pre-fills by field key: template default (manual) or
   *  the computed value (derived), resolved by the parent. */
  fieldDefaults?: Record<string, string>;
}

/**
 * The report's generation-time content fields — what goes IN the report.
 * (The PDF password isn't one of them: it's a property of the file, so it
 * lives in the press bay beside the Generate button that applies it.)
 *
 * Labels sit above full-width inputs: the pane is a ~45% column, too narrow
 * for a label column beside a capped input, and stacking lets long template
 * field names wrap without squeezing what the user is typing.
 */
const ActionPanel: React.FC<ActionPanelProps> = ({ editableFields = [], fieldDefaults = {} }) => {
  const { reportDate, setReportDate, fieldValues, setFieldValue } = useContext(ReportContext);

  const [localDate, setLocalDate] = useState(reportDate || toDateString(new Date()));

  useEffect(() => {
    // if reportDate isn't set in context, default it to today
    if (!reportDate || reportDate === '') {
      setReportDate(toDateString(new Date()));
      setLocalDate(toDateString(new Date()));
    } else {
      setLocalDate(reportDate);
    }
  }, [reportDate]);

  // Child-name input + "Show report header" checkbox are temporarily
  // hidden — kept out of the panel for now while we figure out the
  // longer-term flow. Header rendering is driven from elsewhere
  // (defaults to false unless explicitly toggled by parent state).

  // Touched inputs live in fieldValues; untouched ones show the parent's
  // resolved pre-fill (same precedence the resolver applies when rendering).
  const effectiveValue = (field: HeaderField): string =>
    field.key in fieldValues
      ? fieldValues[field.key]
      : (fieldDefaults[field.key] ?? field.defaultValue ?? '');

  return (
    <div className="rd-fields">
      <div className="rd-field">
        <label className="rd-field__label" htmlFor="rd-report-date">Report date</label>
        <input
          id="rd-report-date"
          type="date"
          className={`rd-input ${!localDate ? 'is-missing' : ''}`}
          value={localDate}
          onChange={(e) => {
            setLocalDate(e.target.value);
            setReportDate(e.target.value);
          }}
        />
      </div>

      {editableFields.map((field) => {
        const value = effectiveValue(field);
        const missingRequired = !!field.required && !value.trim();
        return (
          <div key={field.key} className="rd-field">
            <label className="rd-field__label" htmlFor={`rd-field-${field.key}`}>
              {field.label}
              {field.required
                ? <span className="rd-field__required" title="Required"> *</span>
                : <span className="rd-field__optional">Optional</span>}
            </label>
            <input
              id={`rd-field-${field.key}`}
              type="text"
              className={`rd-input ${missingRequired ? 'is-missing' : ''}`}
              placeholder={field.required ? 'Required' : 'Leave blank to omit'}
              value={value}
              onChange={(e) => setFieldValue(field.key, e.target.value)}
              aria-required={field.required || undefined}
              // Template-defined header fields carry labels like "Lead Teacher"
              // and "Key Worker", which browser heuristics read as name/contact
              // boxes and fill with saved addresses and email addresses. Whatever
              // lands here is printed onto a child's report, so autofill must
              // never put anything in it that the author didn't type.
              autoComplete="off"
            />
          </div>
        );
      })}

    </div>
  );
};

export default ActionPanel;
